import { AfterViewInit, Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonTitle,
  IonToolbar,
  NavController,
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  WorkDocument,
  WorkDocumentsGeneratorV3Service,
  WorkDocumentsV2Service,
  WorkDocxOutputFormatEnum,
  WorkProject,
  WorkProjectsV2Service,
  WorkTask,
  WorkTasksV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import dayjs from 'dayjs';
import { BadgeComponent, ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { GetLabelVariantForStatusPipe, PrependServerToFileSrcPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { ModalComponent } from '@work-frontend-monorepo/work-ui/modals';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { DokumenteViewerComponent } from '@work-frontend-monorepo/work-ui/documents';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';

@Component({
  selector: 'sfg-app-projekte-details-erstinstruktion-page',
  templateUrl: './projekte-details-erstinstruktion-page.component.html',
  styleUrls: ['./projekte-details-erstinstruktion-page.component.scss'],
  standalone: true,
  imports: [
    IonContent,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonMenuButton,
    IonButtons,
    TranslatePipe,
    IonBackButton,
    ButtonComponent,
    BadgeComponent,
    GetLabelVariantForStatusPipe,
    ModalComponent,
    DokumenteViewerComponent,
  ],
})
export class ProjekteDetailsErstinstruktionPageComponent implements OnInit, AfterViewInit {
  @ViewChild('signatureCanvas', { static: false }) signatureCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('dokumenteViewerModal', { static: false }) dokumenteViewerModal!: ModalComponent;

  project = signal<WorkProject | undefined>(undefined);
  task = signal<WorkTask | undefined>(undefined);
  documents = signal<WorkDocument[]>([]);
  activeDocument = signal<WorkDocument | undefined>(undefined);
  hasSignature = signal<boolean>(false);
  uploadLoadingState = signal<LoadingAndFeedbackState>('none');
  isDrawing = false;
  isCompleting = false;

  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly workProjectsV2Service = inject(WorkProjectsV2Service);
  private readonly workTasksV2Service = inject(WorkTasksV2Service);
  private readonly workDocumentsV2Service = inject(WorkDocumentsV2Service);
  private readonly workDocumentsGeneratorV3Service = inject(WorkDocumentsGeneratorV3Service);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navController = inject(NavController);
  private readonly store = inject(Store);
  private readonly prependServerToFileSrcPipe = inject(PrependServerToFileSrcPipe);

  private canvasContext: CanvasRenderingContext2D | null = null;

  protected profile = toSignal(this.store.select(ProfileState.profile));

  async ngOnInit() {
    const projectId = this.activatedRoute.snapshot.params['projectId'];
    if (projectId) {
      await this.loadProjectDetails(projectId);
      await this.loadTask(projectId);
      await this.loadDocuments();
    }
  }

  ngAfterViewInit() {
    // Warte einen Moment damit das Canvas gerendert ist
    setTimeout(() => {
      this.initializeCanvas();
    }, 100);
  }

  private initializeCanvas() {
    if (this.signatureCanvas) {
      const canvas = this.signatureCanvas.nativeElement;

      // Setze die tatsächliche Canvas-Größe (nicht nur CSS)
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;

      this.canvasContext = canvas.getContext('2d');
      if (this.canvasContext) {
        // Fülle Canvas mit weißem Hintergrund
        this.canvasContext.fillStyle = '#FFFFFF';
        this.canvasContext.fillRect(0, 0, canvas.width, canvas.height);

        // Setze Zeichenstil
        this.canvasContext.strokeStyle = '#000000';
        this.canvasContext.lineWidth = 3;
        this.canvasContext.lineCap = 'round';
        this.canvasContext.lineJoin = 'round';
      }
    }
  }

  protected openDocument(document: WorkDocument) {
    this.activeDocument.set(document);
    this.dokumenteViewerModal.open();
  }

  protected startDrawing(event: TouchEvent | MouseEvent) {
    this.isDrawing = true;
    const canvas = this.signatureCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();

    let x: number, y: number;
    if (event instanceof TouchEvent) {
      x = event.touches[0].clientX - rect.left;
      y = event.touches[0].clientY - rect.top;
    } else {
      x = event.clientX - rect.left;
      y = event.clientY - rect.top;
    }

    if (this.canvasContext) {
      this.canvasContext.beginPath();
      this.canvasContext.moveTo(x, y);
    }
  }

  protected draw(event: TouchEvent | MouseEvent) {
    if (!this.isDrawing) return;

    const canvas = this.signatureCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();

    let x: number, y: number;
    if (event instanceof TouchEvent) {
      event.preventDefault();
      x = event.touches[0].clientX - rect.left;
      y = event.touches[0].clientY - rect.top;
    } else {
      x = event.clientX - rect.left;
      y = event.clientY - rect.top;
    }

    if (this.canvasContext) {
      this.canvasContext.lineTo(x, y);
      this.canvasContext.stroke();
      this.hasSignature.set(true);
    }
  }

  protected stopDrawing() {
    this.isDrawing = false;
  }

  protected clearSignature() {
    if (this.canvasContext && this.signatureCanvas) {
      const canvas = this.signatureCanvas.nativeElement;
      // Fülle Canvas wieder mit weißem Hintergrund
      this.canvasContext.fillStyle = '#FFFFFF';
      this.canvasContext.fillRect(0, 0, canvas.width, canvas.height);
      this.hasSignature.set(false);
    }
  }

  protected async completeTask() {
    const currentTask = this.task();
    if (!currentTask) {
      return;
    }

    this.isCompleting = true;
    this.uploadLoadingState.set('loading');

    try {
      const now = dayjs().toISOString();

      // Konvertiere die Unterschrift in eine Base64 URL
      let signatureBase64 = '';
      if (this.hasSignature() && this.signatureCanvas) {
        const canvas = this.signatureCanvas.nativeElement;
        signatureBase64 = canvas.toDataURL('image/png');
      }

      // Generiere das PDF mit der Unterschrift und speichere es im Projekt
      if (signatureBase64) {
        await lastValueFrom(
          this.workDocumentsGeneratorV3Service.documentsGeneratorV3ControllerGenerateFromTemplate({
            format: WorkDocxOutputFormatEnum.Pdf,
            body: {
              fileName: 'ERSTINSTRUKTION.docx',
              customData: {
                datum: dayjs().format('DD.MM.YYYY'),
                signatureUrl: signatureBase64,
              },
              documenttypeId: 17,
              projectId: this.project()?.id,
              withBackground: true,
              saveAsDocument: true,
            },
          }),
        );
      }

      // Task als erledigt markieren
      await lastValueFrom(
        this.workTasksV2Service.tasksV2ControllerUpdate({
          id: currentTask.id,
          body: {
            active: currentTask.active ?? true,
            mandatory: currentTask.mandatory ?? false,
            name: currentTask.name ?? '',
            tasktypeId: currentTask.tasktypeId ?? 0,
            userId: this.profile()?.id ?? currentTask.userId ?? null,
            start: currentTask.start ?? now,
            end: now,
            status: 'Erledigt',
          },
        }),
      );

      this.uploadLoadingState.set('success');

      // Navigiere zurück zur Projektdetailseite mit back animation
      await this.navController.navigateBack(['/mitarbeitende/projekte/details', this.project()?.id], {
        animated: true,
        animationDirection: 'back',
      });
    } catch (completeTaskException) {
      this.uploadLoadingState.set('error');
      this.exceptionHandlingService.handleExceptionWithAlert('completeTaskException', completeTaskException);
    } finally {
      this.uploadLoadingState.set('none');
      this.isCompleting = false;
    }
  }

  private async loadProjectDetails(projectId: number) {
    try {
      const projectResponse = await lastValueFrom(
        this.workProjectsV2Service.projectsV2ControllerFindOneById({
          id: projectId,
          withAllDetails: true,
        }),
      );
      this.project.set(projectResponse);
    } catch (loadProjectException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadProjectException', loadProjectException);
    }
  }

  private async loadDocuments() {
    try {
      const documentsResponse = await lastValueFrom(
        this.workDocumentsV2Service.documentsV2ControllerFindAllPaginated({
          'filter.documentsubtypeId': ['$eq:10'],
          'filter.active': ['$eq:1'],
          limit: 100,
        }),
      );
      this.documents.set(documentsResponse.data ?? []);
    } catch (loadDocumentsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadDocumentsException', loadDocumentsException);
    }
  }

  private async loadTask(projectId: number) {
    try {
      const standardTaskId = 18;

      const tasksResponse = await lastValueFrom(
        this.workTasksV2Service.tasksV2ControllerFindAllTasksForProjectsAndStandardtasks({
          body: {
            projectIds: [projectId],
            standardTaskIds: [standardTaskId],
          },
        }),
      );

      if (tasksResponse.length > 0) {
        this.task.set(tasksResponse[0]);
      }
    } catch (loadTaskException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadTaskException', loadTaskException);
    }
  }
}
